import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2, Link2 } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import CampusEditor, { blankCampus, validateCampuses } from "../components/CampusEditor";

const toCampusPayload = (campuses) => (campuses || []).map((c) => ({
  id: c.id || "", name: c.name, country: c.country, country_code: c.country_code,
  state: c.state, city: c.city, zip: c.zip, email: c.email || "", phone: c.phone || "",
  address_line1: c.line1 || c.address_line1 || "", address_line2: c.line2 || c.address_line2 || "",
  courses: (c.courses || []).filter((co) => co.name && co.name.trim()).map((co) => ({ id: co.id || "", name: co.name, level: co.level || "", stream: co.stream || "", fields: (co.fields || []).filter(Boolean) })),
}));
const fromCampuses = (list) => (list || []).map((c) => ({ ...c, line1: c.address_line1 || c.line1 || "", line2: c.address_line2 || c.line2 || "", courses: c.courses || [] }));

export default function Institutions() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [unis, setUnis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", country: "", featured: false, blurb: "", referral_code: "", referral_fee: 0, referral_fee_pct: 0, referral_currency: "USD", affiliated_universities: [], campuses: [] });
  const [saving, setSaving] = useState(false);
  const [geoCountries, setGeoCountries] = useState([]);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/institutions"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => {
    load();
    api.get("/geo/countries").then((r) => setGeoCountries(r.data)).catch(() => {});
    api.get("/universities").then((r) => setUnis(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setForm({ name: "", country: "", featured: false, blurb: "", referral_code: "", referral_fee: 0, referral_fee_pct: 0, referral_currency: "USD", affiliated_universities: [], campuses: [blankCampus()] }); setEditing(null); setOpen(true); };
  const openEdit = (r) => {
    const campuses = fromCampuses(r.campuses);
    setForm({ name: r.name || "", country: r.country || "", featured: !!r.featured, blurb: r.blurb || "", referral_code: r.referral_code || "", referral_fee: r.referral_fee || 0, referral_fee_pct: r.referral_fee_pct || 0, referral_currency: r.referral_currency || "USD", affiliated_universities: r.affiliated_universities || [], campuses: campuses.length ? campuses : [blankCampus()] });
    setEditing(r); setOpen(true);
  };

  const toggleUni = (id) => setForm((f) => ({ ...f, affiliated_universities: f.affiliated_universities.includes(id) ? f.affiliated_universities.filter((x) => x !== id) : [...f.affiliated_universities, id] }));

  const save = async () => {
    if (!form.name.trim()) { toast({ title: "Institution name is required", variant: "destructive" }); return; }
    const campusErrs = validateCampuses(form.campuses);
    if (campusErrs.length) { toast({ title: "Fix campus details", description: campusErrs[0], variant: "destructive" }); return; }
    setSaving(true);
    const payload = { name: form.name, country: form.country, featured: form.featured, blurb: form.blurb, referral_code: form.referral_code, referral_fee: parseFloat(form.referral_fee) || 0, referral_fee_pct: parseFloat(form.referral_fee_pct) || 0, referral_currency: form.referral_currency, affiliated_universities: form.affiliated_universities, campuses: toCampusPayload(form.campuses) };
    try {
      if (editing) await api.put(`/institutions/${editing.id}`, payload);
      else await api.post("/institutions", payload);
      toast({ title: `Institution ${editing ? "updated" : "created"}` });
      setOpen(false); load();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const del = async (r) => { if (!window.confirm(`Delete "${r.name}"?`)) return; await api.delete(`/institutions/${r.id}`); toast({ title: "Deleted" }); load(); };

  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Institutions</h1>
          <p className="text-slate-500 mt-1 text-sm">Colleges, feeder schools &amp; pathway providers — each affiliated to one or more universities, with its own campuses.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-institution-btn" className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={18} className="mr-1" /> Add Institution</Button>
      </div>

      <div className="relative max-w-sm mt-5">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search institutions..." className="pl-10 h-11 rounded-full" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 mt-5 overflow-x-auto">
        {loading ? <div className="p-12 text-center text-slate-400"><Loader2 className="animate-spin inline" /> Loading…</div> : (
          <table className="w-full text-sm">
            <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
              <th className="px-4 py-3 font-semibold">Institution</th><th className="px-4 py-3 font-semibold">Country</th>
              <th className="px-4 py-3 font-semibold">Campuses</th><th className="px-4 py-3 font-semibold">Affiliated Universities</th><th className="px-4 py-3" />
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-[#f7faf8]">
                  <td className="px-4 py-3 font-medium text-[#012a1e]">{r.name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.country || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.campuses || []).length || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.affiliated || []).map((a) => a.name).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(r)} data-testid={`edit-inst-${r.id}`} className="text-slate-500 hover:text-[#004030] p-1.5"><Pencil size={16} /></button>
                    <button onClick={() => del(r)} className="text-slate-500 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No institutions found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="institution-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} Institution</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Institution Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="inst-name" /></div>
              <div className="space-y-1"><Label>Primary Country</Label>
                <select value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="inst-country">
                  <option value="">Select country</option>
                  {geoCountries.map((c) => <option key={c.iso2} value={c.name}>{c.emoji} {c.name}</option>)}
                </select></div>
            </div>
            <div className="space-y-1"><Label>Short blurb</Label><Input value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} className="h-11" /></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1"><Label>Referral Code</Label><Input value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} className="h-11" placeholder="Auto if blank" data-testid="inst-referral-code" /></div>
              <div className="space-y-1"><Label>Referral Fee (amount)</Label><Input type="number" value={form.referral_fee} onChange={(e) => setForm({ ...form, referral_fee: e.target.value })} className="h-11" data-testid="inst-referral-fee" /></div>
              <div className="space-y-1"><Label>Referral Fee %</Label><Input type="number" value={form.referral_fee_pct} onChange={(e) => setForm({ ...form, referral_fee_pct: e.target.value })} className="h-11" placeholder="e.g. 15" data-testid="inst-referral-fee-pct" /></div>
              <div className="space-y-1"><Label>Currency</Label>
                <select value={form.referral_currency} onChange={(e) => setForm({ ...form, referral_currency: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="inst-referral-currency">
                  {["USD", "GBP", "EUR", "AUD", "CAD", "SGD", "MYR", "INR", "AED"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <Label className="flex items-center gap-1.5 mb-2"><Link2 size={15} /> Affiliated Universities</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="affiliated-universities">
                {unis.map((u) => {
                  const on = form.affiliated_universities.includes(u.id);
                  return <button key={u.id} type="button" onClick={() => toggleUni(u.id)} data-testid={`affiliate-${u.id}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${on ? "bg-[#004030] text-white border-[#004030]" : "bg-white text-slate-600 border-slate-300 hover:border-[#004030]"}`}>{u.name}</button>;
                })}
                {!unis.length && <span className="text-xs text-slate-400">Add universities first to affiliate them.</span>}
              </div>
            </div>

            <div className="flex items-center justify-between"><Label>Featured</Label><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /></div>
            <div className="border-t border-slate-100 pt-3">
              <CampusEditor value={form.campuses} onChange={(c) => setForm((f) => ({ ...f, campuses: c }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-institution" className="bg-[#004030] hover:bg-[#013d2b] text-white">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Institution"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
